import React, { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Plus, MapPin, Edit3, Trash2, X, Save } from 'lucide-react'
import LoadingSpinner from '../LoadingSpinner'
import Currency from '../Currency'
import { locationsService } from '../../services/api'

const CitiesTab = () => {
  const queryClient = useQueryClient()
  const [showModal, setShowModal] = useState(false)
  const [editingCity, setEditingCity] = useState(null)
  const [cityForm, setCityForm] = useState({
    name: '',
    country_id: '',
    governorate_id: '',
    shipping_fee: 0.00
  })

  // Filter states
  const [filters, setFilters] = useState({
    country_id: '',
    governorate_id: ''
  })

  // Bulk edit states
  const [showBulkEdit, setShowBulkEdit] = useState(false)
  const [selectedCities, setSelectedCities] = useState([])
  const [bulkShippingFee, setBulkShippingFee] = useState('')

  // Fetch cities using React Query
  const { data: cities = [], isLoading: citiesLoading } = useQuery({
    queryKey: ['admin-cities'],
    queryFn: () => locationsService.getCities()
  })

  // Fetch countries for dropdown
  const { data: countries = [] } = useQuery({
    queryKey: ['admin-countries'],
    queryFn: () => locationsService.getCountries()
  })

  // Fetch governorates based on selected country (for form)
  const { data: governorates = [] } = useQuery({
    queryKey: ['admin-governorates', cityForm.country_id],
    queryFn: () => locationsService.getGovernorates(cityForm.country_id),
    enabled: !!cityForm.country_id
  })

  // Fetch governorates for filter dropdown
  const { data: filterGovernorates = [] } = useQuery({
    queryKey: ['admin-filter-governorates', filters.country_id],
    queryFn: () => locationsService.getGovernorates(filters.country_id),
    enabled: !!filters.country_id
  })

  // Filter cities based on selected filters
  const filteredCities = cities.filter(city => {
    if (filters.country_id && city.country_id !== parseInt(filters.country_id)) {
      return false
    }
    if (filters.governorate_id && city.governorate_id !== parseInt(filters.governorate_id)) {
      return false
    }
    return true
  })

  // Create city mutation
  const createCityMutation = useMutation({
    mutationFn: (cityData) => locationsService.createCity(cityData),
    onSuccess: () => {
      queryClient.invalidateQueries(['admin-cities'])
      setShowModal(false)
      setCityForm({ name: '', country_id: '', governorate_id: '', shipping_fee: 0.00 })
    }
  })

  // Update city mutation
  const updateCityMutation = useMutation({
    mutationFn: ({ id, data }) => locationsService.updateCity(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries(['admin-cities'])
      setShowModal(false)
      setEditingCity(null)
      setCityForm({ name: '', country_id: '', governorate_id: '', shipping_fee: 0.00 })
    }
  })

  const deleteMutation = useMutation({
    mutationFn: (id) => locationsService.deleteCity(id),
    onSuccess: () => {
      queryClient.invalidateQueries(['admin-cities'])
    }
  })

  // Bulk update mutation
  const bulkUpdateMutation = useMutation({
    mutationFn: async ({ cityIds, shippingFee }) => {
      const promises = cityIds.map(cityId => {
        const city = cities.find(c => c.id === cityId)
        return locationsService.updateCity(cityId, {
          name: city.name,
          country_id: city.country_id,
          governorate_id: city.governorate_id,
          shipping_fee: shippingFee
        })
      })
      return Promise.all(promises)
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['admin-cities'])
      setSelectedCities([])
      setShowBulkEdit(false)
      setBulkShippingFee('')
    }
  })

  const onAddCity = () => {
    setEditingCity(null)
    setCityForm({ name: '', country_id: '', governorate_id: '', shipping_fee: 5.00 })
    setShowModal(true)
  }

  const onEditCity = (city) => {
    setEditingCity(city)
    setCityForm({
      name: city.name,
      country_id: city.country_id,
      governorate_id: city.governorate_id || '',
      shipping_fee: city.shipping_fee
    })
    setShowModal(true)
  }

  const onDeleteCity = async (cityId) => {
    const city = cities.find(c => c.id === cityId)
    if (window.confirm(`هل أنت متأكد من حذف مدينة ${city?.name}؟`)) {
      deleteMutation.mutate(cityId)
    }
  }

  const handleSubmit = (e) => {
    e.preventDefault()
    if (editingCity) {
      updateCityMutation.mutate({ id: editingCity.id, data: cityForm })
    } else {
      createCityMutation.mutate(cityForm)
    }
  }

  const handleCitySelect = (cityId, isSelected) => {
    if (isSelected) {
      setSelectedCities(prev => [...prev, cityId])
    } else {
      setSelectedCities(prev => prev.filter(id => id !== cityId))
    }
  }

  const handleSelectAll = (isSelected) => {
    if (isSelected) {
      setSelectedCities(filteredCities.map(city => city.id))
    } else {
      setSelectedCities([])
    }
  }

  const handleBulkUpdate = () => {
    if (selectedCities.length === 0) {
      alert('Please select cities to update')
      return
    }
    if (!bulkShippingFee) {
      alert('Please enter shipping fee')
      return
    }
    bulkUpdateMutation.mutate({
      cityIds: selectedCities,
      shippingFee: parseFloat(bulkShippingFee)
    })
  }
  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-semibold text-gray-900">إدارة المدن</h2>
        <div className="flex space-x-2">
          {selectedCities.length > 0 && (
            <button 
              onClick={() => setShowBulkEdit(true)}
              className="btn btn-secondary"
            >
              Bulk Edit ({selectedCities.length})
            </button>
          )}
          <button 
            onClick={onAddCity}
            className="btn btn-primary"
          >
            <Plus className="h-4 w-4 mr-2" />
            إضافة مدينة
          </button>
        </div>
      </div>

      {/* Filter Controls */}
      <div className="bg-gray-50 p-4 rounded-lg border">
        <h3 className="text-sm font-medium text-gray-700 mb-3">Filter Cities</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Country
            </label>
            <select
              value={filters.country_id}
              onChange={(e) => setFilters(prev => ({ country_id: e.target.value, governorate_id: '' }))}
              className="w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="">All Countries</option>
              {countries.map(country => (
                <option key={country.id} value={country.id}>
                  {country.name}
                </option>
              ))}
            </select>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Governorate
            </label>
            <select
              value={filters.governorate_id}
              onChange={(e) => setFilters(prev => ({ ...prev, governorate_id: e.target.value }))}
              className="w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
              disabled={!filters.country_id}
            >
              <option value="">All Governorates</option>
              {filterGovernorates.map(governorate => (
                <option key={governorate.id} value={governorate.id}>
                  {governorate.name}
                </option>
              ))}
            </select>
          </div>

          <div className="flex items-end">
            <button
              onClick={() => setFilters({ country_id: '', governorate_id: '' })}
              className="btn btn-outline w-full"
            >
              Clear Filters
            </button>
          </div>
        </div>
      </div>

      <div className="bg-white shadow rounded-lg border">
        <div className="px-6 py-4 border-b">
          <div className="flex justify-between items-center">
            <h3 className="text-lg font-medium text-gray-900">
              Cities ({filteredCities.length} of {cities.length})
            </h3>
            {(filters.country_id || filters.governorate_id) && (
              <span className="text-sm text-blue-600">
                Filtered results
              </span>
            )}
          </div>
        </div>
        <div className="px-6 py-4">
          {citiesLoading ? (
            <div className="flex justify-center py-8">
              <LoadingSpinner size="lg" />
            </div>
          ) : filteredCities && filteredCities.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hidden md:table-cell">
                      <input
                        type="checkbox"
                        checked={selectedCities.length === filteredCities.length && filteredCities.length > 0}
                        onChange={(e) => handleSelectAll(e.target.checked)}
                        className="rounded border-gray-300 text-blue-600 shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
                      />
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      City Name
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hidden md:table-cell">
                      Country
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hidden md:table-cell">
                      Governorate
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hidden md:table-cell">
                      Shipping Fee
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hidden md:table-cell">
                      Created
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredCities.map((city) => (
                    <tr key={city.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap hidden md:table-cell">
                        <input
                          type="checkbox"
                          checked={selectedCities.includes(city.id)}
                          onChange={(e) => handleCitySelect(city.id, e.target.checked)}
                          className="rounded border-gray-300 text-blue-600 shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
                        />
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">{city.name}</div>
                        {/* Mobile-only additional info - simplified */}
                        <div className="md:hidden text-xs text-gray-500 mt-1">
                          <div>{city.country_name}</div>
                          <div><Currency amount={parseFloat(city.shipping_fee || 5.00)} /></div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap hidden md:table-cell">
                        <div className="text-sm text-gray-500">{city.country_name}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap hidden md:table-cell">
                        <div className="text-sm text-gray-500">{city.governorate_name || 'N/A'}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap hidden md:table-cell">
                        <div className="text-sm text-gray-900 font-medium">
                          <Currency amount={parseFloat(city.shipping_fee || 5.00)} />
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap hidden md:table-cell">
                        <div className="text-sm text-gray-500">
                          {new Date(city.created_at).toLocaleDateString()}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <div className="flex flex-col space-y-1 md:flex-row md:space-y-0 md:space-x-2">
                          <button 
                            onClick={() => onEditCity(city)}
                            className="text-indigo-600 hover:text-indigo-900 p-1 rounded hover:bg-indigo-50 flex items-center justify-center"
                            title="Edit City"
                          >
                            <Edit3 className="h-4 w-4" />
                          </button>
                          <button 
                            onClick={() => onDeleteCity(city.id)}
                            className="text-red-600 hover:text-red-900 p-1 rounded hover:bg-red-50 flex items-center justify-center"
                            title="Delete City"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-8">
              <MapPin className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900">No Cities Yet</h3>
              <p className="mt-1 text-sm text-gray-500">
                أضف مدن لمساعدة العملاء في اختيار مواقع التسليم.
              </p>
              <button 
                onClick={onAddCity}
                className="mt-4 btn btn-primary"
              >
                <Plus className="h-4 w-4 mr-2" />
                أضف مدينتك الأولى
              </button>
            </div>
          )}
        </div>
      </div>

      {/* City Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-gray-900">
                {editingCity ? 'تعديل المدينة' : 'إضافة مدينة جديدة'}
              </h3>
              <button
                onClick={() => setShowModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  اسم المدينة
                </label>
                <input
                  type="text"
                  value={cityForm.name}
                  onChange={(e) => setCityForm(prev => ({ ...prev, name: e.target.value }))}
                  className="w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                  required
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  الدولة
                </label>
                <select
                  value={cityForm.country_id}
                  onChange={(e) => setCityForm(prev => ({ ...prev, country_id: e.target.value, governorate_id: '' }))}
                  className="w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                  required
                >
                  <option value="">اختر الدولة</option>
                  {countries.map(country => (
                    <option key={country.id} value={country.id}>
                      {country.name}
                    </option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  المحافظة
                </label>
                <select
                  value={cityForm.governorate_id}
                  onChange={(e) => setCityForm(prev => ({ ...prev, governorate_id: e.target.value }))}
                  className="w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                  disabled={!cityForm.country_id}
                >
                  <option value="">اختر المحافظة (اختياري)</option>
                  {governorates.map(governorate => (
                    <option key={governorate.id} value={governorate.id}>
                      {governorate.name}
                    </option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  رسوم الشحن
                </label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={cityForm.shipping_fee}
                  onChange={(e) => setCityForm(prev => ({ ...prev, shipping_fee: parseFloat(e.target.value) || 0 }))}
                  className="w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                  required
                />
              </div>
              
              <div className="flex space-x-3 pt-4">
                <button
                  type="submit"
                  disabled={createCityMutation.isLoading || updateCityMutation.isLoading}
                  className="btn btn-primary flex-1"
                >
                  <Save className="h-4 w-4 mr-2" />
                  {(createCityMutation.isLoading || updateCityMutation.isLoading) ? 'حفظ...' : 'حفظ'}
                </button>
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="btn btn-outline"
                >
                  إلغاء
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Bulk Edit Modal */}
      {showBulkEdit && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-gray-900">
                Bulk Update Shipping Fees
              </h3>
              <button
                onClick={() => setShowBulkEdit(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            
            <div className="space-y-4">
              <div>
                <p className="text-sm text-gray-600 mb-2">
                  Selected cities: {selectedCities.length}
                </p>
                <div className="max-h-32 overflow-y-auto bg-gray-50 p-2 rounded">
                  {selectedCities.map(cityId => {
                    const city = cities.find(c => c.id === cityId)
                    return (
                      <div key={cityId} className="text-xs text-gray-700">
                        {city?.name}
                      </div>
                    )
                  })}
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  New Shipping Fee (KD)
                </label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={bulkShippingFee}
                  onChange={(e) => setBulkShippingFee(e.target.value)}
                  className="w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Enter shipping fee"
                  required
                />
              </div>
              
              <div className="flex space-x-3 pt-4">
                <button
                  type="button"
                  onClick={handleBulkUpdate}
                  disabled={bulkUpdateMutation.isLoading || !bulkShippingFee}
                  className="btn btn-primary flex-1"
                >
                  {bulkUpdateMutation.isLoading ? 'Updating...' : 'Update All'}
                </button>
                <button
                  type="button"
                  onClick={() => setShowBulkEdit(false)}
                  className="btn btn-outline"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default CitiesTab
